import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useMemo,
  useEffect,
} from 'react';
import { useAppSelector } from 'app/hooks';
import { OS, OK, FORBIDDEN, CANT_VIEW_INCIDENT } from 'utils';
import NoPermisisonPage from 'pages/auth/components/NoPermission';
import SpinCenter from 'components/spin-center';
import { useParams } from 'react-router-dom';
import { getIncidentDetail } from 'api/incidents';
import { history } from 'libs';
import { IResponseIncidentDetail } from '../interfaces';

interface IncidentDetailContextType {
  isInitialLoad: boolean;
  setIsInitialLoad: (value: boolean) => void;
  isLoading: boolean;
  setIsLoading: (value: boolean) => void;
  resetLoadingState: () => void;
  incidentData: IResponseIncidentDetail | null;
  refreshIncidentData: () => void;
}

const IncidentDetailContext = createContext<IncidentDetailContextType | undefined>(undefined);

export const useIncidentDetailContext = () => {
  const context = useContext(IncidentDetailContext);
  if (!context) {
    throw new Error('useIncidentDetailContext must be used within IncidentDetailProvider');
  }
  return context;
};

interface IncidentDetailProviderProps {
  children: ReactNode;
}

export const IncidentDetailProvider: React.FC<IncidentDetailProviderProps> = ({ children }) => {
  const { id }: any = useParams();
  const role: string = useAppSelector((state) => state.auth.role);
  const isLoggedIn: boolean = useAppSelector((state) => state.auth.isLoggedIn);
  const profile: any = useAppSelector((state) => state.auth.profile);

  const isOS: boolean = useMemo(() => role === OS, [role]);
  const isReady: boolean = useMemo(() => {
    if (!isLoggedIn) return true;
    return !!(profile?._id && role);
  }, [isLoggedIn, profile?._id, role]);

  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [incidentData, setIncidentData] = useState<IResponseIncidentDetail | null>(null);

  const refreshIncidentData = useCallback(() => {
    if (!id) return;
    setIsLoading(true);
    getIncidentDetail({ incident_id: id })
      .then((response: any) => {
        if (response?.status === OK && response?.data) {
          setIncidentData(response.data);
        }
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, [id]);

  useEffect(() => {
    if (!isReady || !id) return;
    if (isLoggedIn && isOS) {
      setHasPermission(false);
      setIsInitialLoad(false);
      return;
    }

    setIsLoading(true);
    getIncidentDetail({ incident_id: id })
      .then((response: any) => {
        if (response?.status === OK && response?.data) {
          setHasPermission(true);
          setIncidentData(response.data);
          setIsInitialLoad(false);
          setIsLoading(false);
        } else if (response?.status === 'ERROR') {
          const errorCode = response?.error_code;
          if (errorCode === CANT_VIEW_INCIDENT || errorCode === FORBIDDEN) {
            setHasPermission(false);
            setIsInitialLoad(false);
            setIsLoading(false);
            history.replace({
              pathname: '/admin/no-permission',
            });
          } else {
            setHasPermission(false);
            setIsInitialLoad(false);
            setIsLoading(false);
          }
        } else {
          setHasPermission(false);
          setIsInitialLoad(false);
          setIsLoading(false);
        }
      })
      .catch((error: any) => {
        const errorCode = error?.error_code || error?.response?.data?.error_code;
        if (
          errorCode === CANT_VIEW_INCIDENT ||
          errorCode === FORBIDDEN ||
          error?.response?.status === 403
        ) {
          setHasPermission(false);
          setIsInitialLoad(false);
          setIsLoading(false);
          history.replace({
            pathname: '/admin/no-permission',
          });
        } else {
          // Lỗi khác
          console.log('Provider API error:', error);
          setHasPermission(false);
          setIsInitialLoad(false);
          setIsLoading(false);
        }
      });
  }, [isReady, id, isLoggedIn, isOS]);

  const resetLoadingState = useCallback(() => {
    setIsInitialLoad(false);
    setIsLoading(false);
  }, []);

  const value: IncidentDetailContextType = {
    isInitialLoad,
    setIsInitialLoad,
    isLoading,
    setIsLoading,
    resetLoadingState,
    incidentData,
    refreshIncidentData,
  };

  if (!isReady || isInitialLoad || isLoading) {
    return <SpinCenter size="large" />;
  }

  if ((isLoggedIn && isOS) || hasPermission === false) {
    return <NoPermisisonPage />;
  }

  return <IncidentDetailContext.Provider value={value}>{children}</IncidentDetailContext.Provider>;
};
